import Editor, {
  BeforeMount,
  loader,
  OnMount,
  useMonaco
} from '@monaco-editor/react'
import * as monaco from 'monaco-editor'
import githubLightTheme from 'monaco-themes/themes/GitHub Light.json'
import githubTheme from 'monaco-themes/themes/GitHub.json'
import {nanoid} from 'nanoid'
import React, {useEffect} from 'react'
import {
  EditorWrap,
  Label,
  StyledTextEditor,
  TextEditorWrapper,
  Wrapper
} from '../../../../../components/library/text-editor/styles'
import {Loader} from '../../../../../components/loader'
import {notify} from '../../../../../lib/bugsnag'
import {getTypeDefinition} from './get-type-definition'

loader.config({monaco})

const themes: Record<'atomic' | 'light', monaco.editor.IStandaloneThemeData> = {
  atomic: {
    ...githubTheme,
    colors: {'editor.background': '#f0f2f4'}
  } as monaco.editor.IStandaloneThemeData,
  light: githubLightTheme as monaco.editor.IStandaloneThemeData
}

type ScriptEditorProps = {
  value: string
  onChange?: (v: string) => void
  context?: Record<PropertyKey, unknown>
  height?: string
  readOnly?: boolean
  language?: 'javascript' | 'json'
  onFocus?: () => void
  onBlur?: () => void
  theme?: keyof typeof themes
  className?: string
  lineNumbers?: boolean
}

type ScriptInputProps = ScriptEditorProps & {
  label: string
  upLabel?: string
  style?: React.CSSProperties
}

export const ScriptEditor = ({
  value,
  onChange,
  context,
  height = '200px',
  readOnly,
  language = 'javascript',
  onFocus,
  onBlur,
  theme = 'light',
  className,
  lineNumbers = false
}: ScriptEditorProps) => {
  const monacoEditor = useMonaco()

  // This enables autocomplete for the passed context object
  // Basically we infer the types from the context object and add that type definition as a library
  // Based on a true story https://microsoft.github.io/monaco-editor/playground.html?source=v0.44.0#example-extending-language-services-configure-javascript-defaults
  useEffect(() => {
    if (
      language !== 'javascript' ||
      !monacoEditor ||
      !context ||
      Object.keys(context).length === 0
    ) {
      return
    }
    const contextTypes = Object.entries(context)
      .map(([key, v]) => `declare var ${key}: ${getTypeDefinition(v)}`)
      .join('\n')

    // Name needs to be unique for each ScriptInput
    const id = nanoid(4)
    const contextName = `ts:filename/context-${id}.d.ts`
    let contextLib: monaco.IDisposable
    let contextModel: monaco.editor.ITextModel

    try {
      contextLib =
        monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
          contextTypes,
          contextName
        )
      contextModel = monacoEditor.editor.createModel(
        contextTypes,
        'typescript',
        monacoEditor.Uri.parse(contextName)
      )
    } catch (e) {
      console.error(e)
      notify(e as Error)
    }

    return () => {
      contextLib?.dispose()
      contextModel?.dispose()
    }
  }, [context, language, monacoEditor])

  const onFocusRef = React.useRef(onFocus)
  const onBlurRef = React.useRef(onBlur)
  const onMountDisposeRef = React.useRef<monaco.IDisposable>()

  useEffect(() => {
    onFocusRef.current = onFocus
    onBlurRef.current = onBlur
  }, [onFocus, onBlur])

  const handleEditorDidMount: OnMount = (editor, m) => {
    const disposeFocus = editor.onDidFocusEditorText(() =>
      onFocusRef.current?.()
    )
    const disposeBlur = editor.onDidBlurEditorText(() => onBlurRef.current?.())
    editor.updateOptions({readOnly})
    m.editor.setTheme(theme)

    onMountDisposeRef.current = {
      dispose() {
        disposeBlur.dispose()
        disposeFocus.dispose()
      }
    }
  }

  const onBeforeMount: BeforeMount = m => {
    m.editor.defineTheme(theme, themes[theme])
    m.editor.setTheme(theme)

    // This prevents autocompletion for dom elements and other stuff we don't want to suggest
    m.languages.typescript.javascriptDefaults.setCompilerOptions({
      lib: ['es2015'],
      allowNonTsExtensions: true
    })
  }

  useEffect(() => {
    return () => {
      // clean up focus listeners added in handleEditorDidMount
      onMountDisposeRef.current?.dispose()
    }
  }, [])

  return (
    <Editor
      height={height}
      defaultLanguage={language}
      value={value}
      beforeMount={onBeforeMount}
      onMount={handleEditorDidMount}
      wrapperProps={{className: `script-editor ${className ? className : ''}`}}
      onChange={onChange ? v => onChange(v ?? '') : undefined}
      loading={<Loader />}
      options={{
        lineNumbers: lineNumbers ? 'on' : 'off',
        minimap: {
          enabled: false
        },
        readOnly
      }}
    />
  )
}

export const ScriptInput = ({
  value,
  label,
  upLabel,
  style,
  className,
  readOnly,
  theme = 'atomic',
  ...editorProps
}: ScriptInputProps) => {
  const [focused, setFocused] = React.useState<boolean>(false)
  return (
    <TextEditorWrapper
      //readOnly on the wrapper effects appearance only, hack here to match the the light theme
      readOnly={readOnly || theme === 'light'}
      monacoEditor={true}
      style={style}
      className={className}
    >
      <EditorWrap>
        <Wrapper className={value || focused ? 'labelUp' : ''}>
          <Label style={{zIndex: 1}}>
            {value || focused || readOnly ? (upLabel ? upLabel : label) : label}
          </Label>
          <StyledTextEditor>
            <ScriptEditor
              value={value}
              onBlur={() => setFocused(false)}
              onFocus={() => setFocused(true)}
              readOnly={readOnly}
              lineNumbers={true}
              theme={theme}
              {...editorProps}
            />
          </StyledTextEditor>
        </Wrapper>
      </EditorWrap>
    </TextEditorWrapper>
  )
}
